import { useState, type FormEvent } from "react";
import Layout from "@/components/feature/Layout";
import PageHero from "@/components/feature/PageHero";
import { company } from "@/mocks/company";

type Status = "idle" | "submitting" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState<string>("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const form = event.currentTarget;
    const formData = new FormData(form);

    // ========================================
    // Honeypot（スパム対策）
    // ========================================
    const honey = (formData.get("website_alt") || "").toString().trim();

    if (honey) {
      // Botの場合もエラーを返さず成功扱い
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    formData.delete("website_alt");

    // ========================================
    // 必須項目チェック
    // ========================================
    const requiredFields = ["name", "email", "message"];

    for (const field of requiredFields) {
      const value = (formData.get(field) || "").toString().trim();

      if (!value) {
        setStatus("error");
        setFormError("必須項目が未入力です。ご確認ください。");
        return;
      }
    }

    // ========================================
    // メッセージ文字数チェック
    // ========================================
    const message = (formData.get("message") || "").toString();

    if (message.length > 500) {
      setStatus("error");
      setFormError(
        "お問い合わせ内容は500文字以内でご記入ください。"
      );
      return;
    }

    // ========================================
    // メールアドレス簡易チェック
    // ========================================
    const email = (formData.get("email") || "").toString().trim();

    const emailPattern =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(email)) {
      setStatus("error");
      setFormError(
        "正しいメールアドレスを入力してください。"
      );
      return;
    }

    // ========================================
    // 送信開始
    // ========================================
    setStatus("submitting");
    setFormError("");

    try {
      // ========================================
      // FormData → URLSearchParams
      // ========================================
      const params = new URLSearchParams();

      formData.forEach((value, key) => {
        params.append(key, value.toString());
      });

      // ========================================
      // contact.phpへ送信
      // ========================================
      const response = await fetch("/contact.php", {
        method: "POST",
        headers: {
          "Content-Type":
            "application/x-www-form-urlencoded; charset=UTF-8",
        },
        body: params.toString(),
      });

      // ========================================
      // PHPからのレスポンス取得
      // ========================================
      const responseText = await response.text();

      let parsed: {
        code?: string;
        meta?: {
          message?: string;
          detail?: string;
        };
      } | null = null;

      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      // ========================================
      // メッセージ取得
      // ========================================
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";

      // ========================================
      // PHP側の成功コード確認
      // ========================================
      const okCode = parsed?.code === "OK";

      // ========================================
      // 成功
      // ========================================
      if (response.ok && okCode) {
        setStatus("success");
        setFormError("");
        form.reset();

        // ページ上部へスクロール
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } else {
        // ========================================
        // 送信失敗
        // ========================================
        setStatus("error");

        setFormError(
          serverMsg ||
            "送信に失敗しました。時間をおいて再度お試しいただくか、お電話にてご連絡ください。"
        );
      }
    } catch {
      // ========================================
      // 通信エラー
      // ========================================
      setStatus("error");

      setFormError(
        "通信エラーが発生しました。ネットワーク状況をご確認ください。"
      );
    }
  };

  return (
    <Layout>
      {/* ========================================
          ページヒーロー
      ======================================== */}
      <PageHero
        eyebrow="Contact"
        title="お問い合わせ・見積依頼"
        description="現地確認・お見積は完全無料です。24時間以内にご連絡差し上げます（土日祝は翌営業日）。"
        image="images/contact.avif"
      />

      {/* ========================================
          お問い合わせ
      ======================================== */}
      <section className="bg-background-50 py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-4 md:px-10 grid grid-cols-1 md:grid-cols-5 gap-10">

          {/* ======================================
              左側
          ====================================== */}
          <div className="md:col-span-2 space-y-6">

            {/* 電話 */}
            <div className="bg-primary-900 text-background-50 rounded-lg p-7">
              <h3 className="font-heading text-xl mb-4">
                お電話でのご相談
              </h3>

              <a
                href="tel:092-984-5214"
                className="font-heading text-3xl text-accent-400 mb-2 block cursor-pointer"
              >
                {company.phone}
              </a>

              <p className="text-sm text-background-200 leading-relaxed">
                {company.hours}
                <br />
                （時間外はフォームよりお願いいたします）
              </p>
            </div>

            {/* 相談内容 */}
            <div className="bg-background-100 border border-background-200 rounded-lg p-7">
              <h3 className="font-heading text-lg text-foreground-950 mb-3">
                こんな時にご相談ください
              </h3>

              <ul className="space-y-2 text-sm text-foreground-800">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-primary-600 mt-0.5"></i>
                  今の清掃会社の料金・品質を見直したい
                </li>

                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-primary-600 mt-0.5"></i>
                  退去後クリーニングを急ぎで依頼したい
                </li>

                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-primary-600 mt-0.5"></i>
                  1棟だけ・小規模物件の相談がしたい
                </li>

                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-primary-600 mt-0.5"></i>
                  相見積で他社と比較検討したい
                </li>
              </ul>
            </div>

            {/* 対応エリア */}
            <div className="bg-background-100 border border-background-200 rounded-lg p-7">
              <h3 className="font-heading text-lg text-foreground-950 mb-3">
                対応エリア
              </h3>

              <p className="text-sm text-foreground-700 leading-relaxed">
                福岡市
                <br />
                久留米市
                <br />
                エリア外もお気軽にご相談ください。
              </p>
            </div>
          </div>

          {/* ======================================
              右側：お問い合わせフォーム
          ====================================== */}
          <div className="md:col-span-3">

            <form
              id="contact-form"
              onSubmit={handleSubmit}
              className="bg-background-100 border border-background-200 rounded-lg p-6 md:p-9"
              noValidate
            >

              <h3 className="font-heading text-xl text-foreground-950 mb-1">
                お問い合わせフォーム
              </h3>

              <p className="text-sm text-foreground-600 mb-6">
                <span className="text-accent-700">*</span>
                {" "}は必須項目です。
              </p>

              {/* ==================================
                  お名前・会社名
              ================================== */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">

                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm text-foreground-800 mb-1.5"
                  >
                    お名前{" "}
                    <span className="text-accent-700">*</span>
                  </label>

                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    maxLength={100}
                    autoComplete="name"
                    className="w-full bg-background-50 border border-background-200 rounded-md px-4 py-3 text-sm text-foreground-900 focus:outline-none focus:border-primary-400"
                    placeholder="山田 太郎"
                  />
                </div>

                <div>
                  <label
                    htmlFor="company_name"
                    className="block text-sm text-foreground-800 mb-1.5"
                  >
                    会社名（任意）
                  </label>

                  <input
                    id="company_name"
                    name="company_name"
                    type="text"
                    maxLength={200}
                    autoComplete="organization"
                    className="w-full bg-background-50 border border-background-200 rounded-md px-4 py-3 text-sm text-foreground-900 focus:outline-none focus:border-primary-400"
                    placeholder="株式会社◯◯"
                  />
                </div>
              </div>

              {/* ==================================
                  メール・電話番号
              ================================== */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm text-foreground-800 mb-1.5"
                  >
                    メールアドレス{" "}
                    <span className="text-accent-700">*</span>
                  </label>

                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    maxLength={254}
                    autoComplete="email"
                    className="w-full bg-background-50 border border-background-200 rounded-md px-4 py-3 text-sm text-foreground-900 focus:outline-none focus:border-primary-400"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm text-foreground-800 mb-1.5"
                  >
                    電話番号（任意）
                  </label>

                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    maxLength={50}
                    autoComplete="tel"
                    className="w-full bg-background-50 border border-background-200 rounded-md px-4 py-3 text-sm text-foreground-900 focus:outline-none focus:border-primary-400"
                    placeholder="090-XXXX-XXXX"
                  />
                </div>
              </div>

              {/* ==================================
                  ご相談内容・物件所在地
              ================================== */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">

                <div>
                  <label
                    htmlFor="service_type"
                    className="block text-sm text-foreground-800 mb-1.5"
                  >
                    ご相談内容
                  </label>

                  <select
                    id="service_type"
                    name="service_type"
                    className="w-full bg-background-50 border border-background-200 rounded-md px-4 py-3 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer"
                    defaultValue="マンション日常清掃"
                  >
                    <option>マンション日常清掃</option>
                    <option>定期清掃</option>
                    <option>空室・退去後クリーニング</option>
                    <option>ガラス・サッシ清掃</option>
                    <option>ゴミ置き場管理</option>
                    <option>スポット・特殊清掃</option>
                    <option>その他</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="property_area"
                    className="block text-sm text-foreground-800 mb-1.5"
                  >
                    物件所在地
                  </label>

                  <input
                    id="property_area"
                    name="property_area"
                    type="text"
                    maxLength={200}
                    autoComplete="street-address"
                    className="w-full bg-background-50 border border-background-200 rounded-md px-4 py-3 text-sm text-foreground-900 focus:outline-none focus:border-primary-400"
                    placeholder="例：福岡県福岡市"
                  />
                </div>
              </div>

              {/* ==================================
                  お問い合わせ内容
              ================================== */}
              <div className="mb-4">

                <label
                  htmlFor="message"
                  className="block text-sm text-foreground-800 mb-1.5"
                >
                  お問い合わせ内容{" "}
                  <span className="text-accent-700">*</span>
                </label>

                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full bg-background-50 border border-background-200 rounded-md px-4 py-3 text-sm text-foreground-900 focus:outline-none focus:border-primary-400"
                  placeholder="物件規模・状況などをご記入いただけますとスムーズです（500文字以内）"
                ></textarea>

                <p className="text-xs text-foreground-500 mt-1">
                  500文字以内
                </p>
              </div>

              {/* ==================================
                  Honeypot
                  通常のユーザーには見えません
              ================================== */}
              <div
                className="hp-field"
                aria-hidden="true"
              >
                <label>
                  Website

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </label>
              </div>

              {/* ==================================
                  エラー表示
              ================================== */}
              {status === "error" && formError && (
                <div className="mb-5 flex items-start gap-2 bg-accent-100 border border-accent-300 text-accent-900 rounded-md px-4 py-3 text-sm">
                  <i className="ri-error-warning-line mt-0.5"></i>

                  <span>{formError}</span>
                </div>
              )}

              {/* ==================================
                  成功表示
              ================================== */}
              {status === "success" && (
                <div className="mb-5 flex items-start gap-2 bg-secondary-100 border border-secondary-300 text-secondary-900 rounded-md px-4 py-3 text-sm">
                  <i className="ri-checkbox-circle-line mt-0.5"></i>

                  <span>
                    お問い合わせありがとうございます。
                    24時間以内にご連絡差し上げます。
                  </span>
                </div>
              )}

              {/* ==================================
                  送信ボタン
              ================================== */}
              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-70 text-background-50 font-medium py-4 rounded-md text-sm md:text-base whitespace-nowrap cursor-pointer transition-colors inline-flex items-center justify-center gap-2"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    送信中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    送信する
                  </>
                )}
              </button>

            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}

